import React from "react"

import styled, { css } from "styled-components"

import { borderRadius, colors, spacing, typography } from "@izettle/otto"

import { SemanticState } from "../../enumerations"
import { InputBase, GenericInputProps } from "../input-base"
import { InputBaseContainerProps } from "../input-base/types"

const mixinInput = css<GenericInputProps>`
  caret-color: ${({ disabled, semanticState, isFocused }) => {
    if (disabled) {
      return
    }

    const semanticColors = {
      [SemanticState.Warning]: isFocused
        ? colors.icon.warning.large
        : colors.text.warning,
      [SemanticState.Error]: isFocused ? colors.icon.error.large : colors.text.error,
      [SemanticState.Success]: isFocused
        ? colors.action.secondary.text.default
        : colors.text.success,
      [SemanticState.Default]: isFocused
        ? colors.action.secondary.text.default
        : colors.text.default,
    }

    const semanticColorsKey: keyof typeof semanticColors =
      semanticState ?? SemanticState.Default

    return semanticColors[semanticColorsKey]
  }};
`

export const InputMemoized = React.memo(styled.input<GenericInputProps>`
  /* Increase specificity for overriding global styles from portal */
  && {
    /* Reset global styles from portal */
    background-color: unset;
    height: unset;
    width: unset;
    /* End rest */

    ${typography.regular}
    margin: 0 ${spacing.small};
    flex: 1;
    border-radius: ${borderRadius.large};
    border: none;
    outline: none;
    padding: 0;
    min-width: 0;
    transition: color 0.1s cubic-bezier(0.25, 0.1, 0.25, 1);
    &:disabled {
      background: none;
      cursor: not-allowed;
      color: ${colors.text.disabled};
      /* required on iOS */
      -webkit-text-fill-color: ${colors.text.disabled};
      opacity: 1;
    }
    :required {
      box-shadow: none;
    }
    ${mixinInput}
    &:-webkit-autofill,
    &:-webkit-autofill:hover, 
    &:-webkit-autofill:focus {
      background-color: ${colors.background.default} !important;
      box-shadow: none;
      -webkit-box-shadow: none;
      transition: background-color 5000s ease-in-out 0s;
    }
  }
`)

export const InputBaseStyled = styled(InputBase)<InputBaseContainerProps>`
  cursor: ${({ isDisabled }) => (isDisabled ? "not-allowed" : "text")};
`
